import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { api } from '../../services/api';
import SessionsManager from './SessionsManager';
import ProgramEnrollments from './ProgramEnrollments';
import ProgramForm from './ProgramForm';

function ProgramDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchProgram();
  }, [id]);

  const fetchProgram = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await api.getProgram(id);
      setProgram(data);
    } catch (error) {
      console.error('Error fetching program:', error);
      setError(error.response?.data?.error || 'Failed to load program');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${program.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.deleteProgram(id);
      navigate('/admin/programs');
    } catch (error) {
      console.error('Error deleting program:', error);
      alert(error.response?.data?.error || 'Failed to delete program');
    }
  };

  const handleSaveComplete = () => {
    setEditMode(false);
    fetchProgram();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
        <Container maxWidth="lg">
          <Alert severity="error">{error}</Alert>
          <Button onClick={() => navigate('/admin/programs')} sx={{ mt: 2 }}>
            Back to Programs
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 12, pb: 4 }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/programs')}
          sx={{ mb: 2 }}
        >
          Back to Programs
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              {program.name}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {program.term}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!editMode && (
              <>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => { setActiveTab(0); setEditMode(true); }}
                >
                  Edit Details
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </>
            )}
            {editMode && (
              <Button
                variant="outlined"
                onClick={() => setEditMode(false)}
              >
                Cancel Editing
              </Button>
            )}
          </Box>
        </Box>

        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon color="primary" />
                  <Typography variant="h6">${program.price.toFixed(2)}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Price
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon color="primary" />
                  <Typography variant="h6">
                    {program.male_capacity} / {program.female_capacity}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Capacity (M / F)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon color="primary" />
                  <Chip
                    label={program.public ? 'Public' : 'Private'}
                    color={program.public ? 'success' : 'default'}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Visibility
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, value) => { setActiveTab(value); setEditMode(false); }}
            variant="fullWidth" aria-label="Program administration sections">
            <Tab label="Program Info" />
            <Tab label="Sessions" />
            <Tab label="Enrollments" />
          </Tabs>
        </Paper>

        {activeTab === 0 && (editMode ? (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
              Edit Program Details
            </Typography>
            <ProgramForm
              isEmbedded
              programId={id}
              onSave={handleSaveComplete}
            />
          </Paper>
        ) : (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              Program Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Location
                </Typography>
                <Typography variant="body1" paragraph>
                  {program.location || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Grade Range
                </Typography>
                <Typography variant="body1" paragraph>
                  {program.min_grade !== null && program.max_grade !== null
                    ? `Grades ${program.min_grade} - ${program.max_grade}`
                    : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1" paragraph>
                  {program.description || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Curriculum
                </Typography>
                <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                  {program.curriculum || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Rules
                </Typography>
                <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                  {program.rules || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Volume Discount
                </Typography>
                <Typography variant="body1">
                  {program.volume_discount ? `$${program.volume_discount}` : '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Minimum Price
                </Typography>
                <Typography variant="body1">
                  {program.minimum_price ? `$${program.minimum_price}` : '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        ))}

        {activeTab === 1 && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <SessionsManager programId={id} />
          </Paper>
        )}

        {activeTab === 2 && (
          <Paper sx={{ p: 3 }}>
            <ProgramEnrollments isEmbedded programId={id} program={program} />
          </Paper>
        )}
      </Container>
    </Box>
  );
}

export default ProgramDetails;
